/**
 * Halving countdown — pure math. NO React imports.
 *
 * Every value here is derived from a block height plus an average block
 * interval, so the countdown degrades in three stages and NEVER shows an
 * error state (see `estimateHalving` + `projectBlockHeight`):
 *
 *   1. live tip height  + observed network block interval  (most accurate)
 *   2. live tip height  + the 600s protocol target
 *   3. no network at all: project the height itself from the last halving
 *
 * Anchor: block 840,000 (the 2024 halving) was mined 2024-04-20 00:09:27 UTC.
 * Anchoring on a halving rather than on "some block I looked up" means the
 * offline projection is exact at the anchor and drifts only with the network's
 * deviation from the 600s target.
 */

/** Blocks per halving epoch. Consensus constant. */
export const HALVING_INTERVAL = 210_000;

/** Protocol target block interval, ms. Bitcoin retargets difficulty to this. */
export const TARGET_BLOCK_MS = 600_000;

/** The 2024 halving, used as the offline anchor. */
export const REFERENCE_HALVING = {
  height: 840_000,
  atMs: Date.UTC(2024, 3, 20, 0, 9, 27),
} as const;

/** Initial block subsidy in BTC. */
const INITIAL_SUBSIDY = 50;

/** Height of the next halving strictly after `height`. */
export function nextHalvingHeight(height: number): number {
  const era = Math.floor(height / HALVING_INTERVAL);
  return (era + 1) * HALVING_INTERVAL;
}

/** Block subsidy in BTC at a given height. */
export function subsidyAt(height: number): number {
  const era = Math.floor(Math.max(0, height) / HALVING_INTERVAL);
  return era >= 33 ? 0 : INITIAL_SUBSIDY / Math.pow(2, era);
}

/**
 * Offline fallback: project a block height from the 2024 halving anchor at the
 * 600s protocol target. Good to roughly +/-1,000 blocks a year out, which is
 * well inside the precision a day-granularity countdown needs.
 */
export function projectBlockHeight(nowMs: number = Date.now()): number {
  const elapsed = nowMs - REFERENCE_HALVING.atMs;
  return REFERENCE_HALVING.height + Math.max(0, Math.round(elapsed / TARGET_BLOCK_MS));
}

export type HalvingSource = 'network' | 'projected';

export interface HalvingEstimate {
  readonly currentHeight: number;
  readonly targetHeight: number;
  readonly blocksRemaining: number;
  /** Epoch ms of the estimated halving. */
  readonly etaMs: number;
  readonly etaDate: Date;
  /** Whole days remaining, rounded up so "1 day" never reads as "0 days". */
  readonly daysRemaining: number;
  readonly subsidyNow: number;
  readonly subsidyAfter: number;
  /** 0..1 through the current halving epoch. */
  readonly epochProgress: number;
  /** Whether the height came from a node or from the offline projection. */
  readonly source: HalvingSource;
  /** Average block interval used, ms. */
  readonly blockMs: number;
}

export interface HalvingInput {
  /** Live tip height. null/undefined => project it from the anchor. */
  readonly height?: number | null;
  /** Observed average block interval, ms. Falls back to the 600s target. */
  readonly blockMs?: number | null;
  readonly nowMs?: number;
}

/** Never throws, never returns null — the countdown has no error state. */
export function estimateHalving(input: HalvingInput = {}): HalvingEstimate {
  const nowMs = input.nowMs ?? Date.now();

  const networkHeight =
    typeof input.height === 'number' && Number.isFinite(input.height) && input.height > 0
      ? Math.floor(input.height)
      : null;
  const currentHeight = networkHeight ?? projectBlockHeight(nowMs);
  const source: HalvingSource = networkHeight === null ? 'projected' : 'network';

  // Clamp a reported interval to a sane 5-20 min band: mempool.space's timeAvg
  // is a short rolling window and spikes hard after a slow difficulty epoch.
  const reported = input.blockMs;
  const blockMs =
    typeof reported === 'number' && Number.isFinite(reported) && reported >= 300_000 && reported <= 1_200_000
      ? reported
      : TARGET_BLOCK_MS;

  const targetHeight = nextHalvingHeight(currentHeight);
  const blocksRemaining = Math.max(0, targetHeight - currentHeight);
  const etaMs = nowMs + blocksRemaining * blockMs;

  return {
    currentHeight,
    targetHeight,
    blocksRemaining,
    etaMs,
    etaDate: new Date(etaMs),
    daysRemaining: Math.max(0, Math.ceil((etaMs - nowMs) / 86_400_000)),
    subsidyNow: subsidyAt(currentHeight),
    subsidyAfter: subsidyAt(targetHeight),
    epochProgress: Math.min(1, Math.max(0, 1 - blocksRemaining / HALVING_INTERVAL)),
    source,
    blockMs,
  };
}

/**
 * "2y 7m" / "7 mo" / "18 days" — the countdown has ~70px of width in the micro
 * strip, so it degrades to coarser units the further out the halving is.
 */
export function formatHalvingDistance(daysRemaining: number): string {
  const days = Math.max(0, Math.round(daysRemaining));
  if (days === 0) return 'today';
  if (days === 1) return '1 day';
  if (days < 45) return `${days} days`;
  const months = Math.round(days / 30.44);
  if (months < 12) return `${months} mo`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem === 0 ? `${years}y` : `${years}y ${rem}m`;
}
